import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { qaqcApi, type PourCard } from '@/lib/apiClient';
import { Button } from '@/components';

// Shared between the per-project QA/QC tab (ProjectWorkspace) and the
// company-wide QA/QC roll-up page: status/type maps, the vendor email, and
// the office action buttons. One implementation so the two surfaces can't
// drift.

export const POUR_STATUS = {
  pending:   { label: 'Pending Office Confirm', color: 'var(--ew-performance-700)', bg: 'var(--ew-performance-50)', dot: 'var(--ew-performance)' },
  confirmed: { label: 'Order Confirmed',        color: 'var(--ew-quality-700)',     bg: 'var(--ew-quality-50)',     dot: 'var(--ew-quality)' },
  poured:    { label: 'Poured',                 color: 'var(--ew-green-700)',       bg: 'var(--ew-green-50)',       dot: 'var(--ew-green)' },
  covered:   { label: 'Confirmed w/ Main Pour',  color: 'var(--ew-green-700)',       bg: 'var(--ew-green-50)',       dot: 'var(--ew-green)' },
  cancelled: { label: 'Cancelled',              color: 'var(--ew-n-500)',           bg: 'var(--ew-n-100)',          dot: 'var(--ew-n-400)' },
} as const;
export const pourStatus = (s: string) => POUR_STATUS[s as keyof typeof POUR_STATUS] ?? POUR_STATUS.pending;

/** A rock order is delivered, not poured. Same status underneath. */
export const pourStatusLabel = (status: string, pourType: string) =>
  pourType === 'rock' && status === 'poured' ? 'Delivered' : pourStatus(status).label;

export const POUR_TYPE_LABEL: Record<string, string> = {
  main: 'Main Pour', mudseal: 'Mud Seal', willcall: 'Will-Call', rock: 'Rock / Aggregate',
};

/** Rock/Aggregate deliveries ride the same cards as concrete but aren't
 *  concrete: no mix, no CY, and the quantity carries its own unit. */
export const isRock = (card: { pourType: string }) => card.pourType === 'rock';

/** Quantity with its unit: concrete is always CY; rock already says its unit.
 *  Tables and the drawer write " CY"; the schedule chip's compact form is "cy".
 *  Text that names its own unit reads as written, so a card switched from rock
 *  shows "40 Tons", never "40 Tons CY". */
const NAMES_UNIT = /\b(tons?|tn|loads?|truckloads?|trucks?|cy|yds?|yards?)\b/i;
const withUnit = (card: { pourType: string; qtyText: string | null }, cy: string) =>
  card.qtyText ? (isRock(card) || NAMES_UNIT.test(card.qtyText) ? card.qtyText : `${card.qtyText}${cy}`) : null;
export const qtyDisplay = (card: { pourType: string; qtyText: string | null }) => withUnit(card, ' CY');
export const qtyLabel = (card: { pourType: string; qtyText: string | null }) => withUnit(card, 'cy') ?? '';

// Shared concrete color language: chip fill = order STATE, then POUR TYPE once
// confirmed (a new order stays white with a black border until confirmed).
// Used by BOTH the Concrete Pour Schedule grid and the work-queue pour-type
// chip so the two views can't drift. Dark text reads on every fill except the
// confirmed-rock brown, which carries its own `fg`.
/** Confirmed rock / aggregate: the chip, the schedule legend and the KPI tile. */
export const ROCK_BROWN = '#8B5A2B';

export type ChipStyle = { bg: string; border: string; strike?: boolean; fg?: string };
export function chipStyle(status: string, pourType: string): ChipStyle {
  if (status === 'cancelled') return { bg: '#00B0F0', border: 'none', strike: true };
  if (status === 'poured')    return { bg: '#92D050', border: 'none' };
  if (status === 'covered')   return { bg: '#92D050', border: 'none' }; // done via main pour (off the schedule; shown only in the work queue)
  if (status === 'confirmed') {
    if (pourType === 'willcall') return { bg: '#FFFF00', border: 'none' };
    if (pourType === 'mudseal')  return { bg: '#BFBFBF', border: 'none' };
    if (pourType === 'rock')     return { bg: ROCK_BROWN, border: 'none', fg: '#FFFFFF' };
    return { bg: '#FFC000', border: 'none' }; // main pour (default)
  }
  return { bg: '#FFFFFF', border: '1px solid var(--ew-dark)' }; // pending / new / unconfirmed
}

/**
 * The job's blanket POs on one line, for the schedule grid and the pour drawer.
 *
 * The PO is typed as it reads on the document, and on the office's own sheet
 * that already includes the material: "Concrete BPO- 26453", "AGG PO- 23681".
 * So the Con/Agg prefix is only added when the value doesn't already say it —
 * otherwise the first thing shipped read "Con CON PO-23682 · Agg AGG PO-23681".
 */
export function bpoLabel(
  project: { concreteBpo?: string | null; aggregateBpo?: string | null } | null | undefined,
): string | null {
  const tag = (value: string | null | undefined, prefix: string, already: RegExp) => {
    const v = value?.trim();
    if (!v) return null;
    return already.test(v) ? v : `${prefix} ${v}`;
  };
  const parts = [
    tag(project?.concreteBpo, 'Con', /^(con|concrete)\b/i),
    tag(project?.aggregateBpo, 'Agg', /^(agg|aggregate)\b/i),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * WALL-CLOCK formatters (timeZone:'UTC' = pure passthrough) for form-authored
 * datetimes: pour_datetime, actual_pour_at, inspect_date. The parser stores
 * the form's Eastern wall-clock digits as UTC (see qaqc-parser fsDate), so
 * rendering them "in UTC" shows exactly what was typed on the form — no
 * browser-zone shift (the PEs' pour-time bug).
 */
export function fmtDt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}
export function fmtWallD(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
/** Wall-clock TIME-ONLY (form-authored datetimes; the schedule grid's chips). */
export function fmtWallT(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}
/** INSTANT formatter (browser zone) — for real server timestamps only
 *  (confirmed_at / poured_at / cancelled_at). */
export function fmtD(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
/** INSTANT date+time (browser zone) — submission timestamps (PE ask: show
 *  the time the checklist came in, not just the day). */
export function fmtDTm(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * "Mix not approved" badge — the ONLY mixMatch state that gets flagged.
 *
 * `unchecked` (no register on the job, or no mix on the card) renders nothing on
 * purpose: a project nobody has filled a register in for has nothing to be wrong
 * about, and badging it would light up every job the day this ships. A badge
 * that's always on is a badge people stop reading — see the At Risk rule where
 * 42% of flags were false and PMs had tuned them out. `approved` renders nothing
 * either; a tick on every correct row is noise.
 */
export function MixFlag({ card, size }: { card: PourCard; size?: 'sm' }) {
  if (card.mixMatch !== 'unapproved' && card.mixMatch !== 'ambiguous') return null;
  const ambiguous = card.mixMatch === 'ambiguous';
  return (
    <span
      style={{
        marginLeft: 8, display: 'inline-block',
        font: `var(--ew-fw-semibold) ${size === 'sm' ? 9.5 : 10}px/1.5 var(--ew-font)`,
        textTransform: 'uppercase', letterSpacing: '.04em',
        background: 'var(--ew-safety-50)', color: 'var(--ew-safety-700)',
        border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-pill)', padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
      title={ambiguous
        ? 'This mix ID appears more than once on the job register — clean up the duplicates.'
        : "This mix ID isn't on the job's approved list. Either the field typed it wrong, or the mix needs approving and adding to the register."}
    >
      {ambiguous ? 'Mix ambiguous' : 'Mix not approved'}
    </span>
  );
}

/** Mail clients truncate very long mailto: URLs (the RFI/Submittal/PPR
 *  generators warn at this same threshold). Order notes are free prose, so this
 *  is the one field here that can realistically blow the budget. */
const MAILTO_BUDGET = 1900;

/** Vendor confirmation email (RFI-style mailto): To: left empty on purpose —
 *  the forms don't capture vendor emails; the PM fills it in Outlook.
 *
 *  The confirmation-call details ride along on purpose (product owner, 2026-07-29): the
 *  written confirmation should name the dispatcher who took the order verbally,
 *  so the vendor's own record points back at the same conversation. */
export function vendorEmailHref(card: PourCard, jobNumber: string): string {
  const rock = isRock(card);
  const subject = `${rock ? 'Rock/Aggregate' : 'Concrete'} Order Confirmation — Job ${jobNumber}${card.element ? ` · ${card.element}` : ''}${card.pourDatetime ? ` · ${fmtDt(card.pourDatetime)}` : ''}`;
  const confirmedWith = [
    card.dispatcherName ? `with ${card.dispatcherName}` : null,
    card.dispatcherPhone ? `at ${card.dispatcherPhone}` : null,
  ].filter(Boolean).join(' ');

  const build = (notes: string | null) => {
    const lines = [
      `This confirms our ${rock ? 'rock/aggregate' : 'concrete'} order${card.vendor ? ` with ${card.vendor}` : ''}:`,
      '',
      `Job: ${jobNumber}`,
      card.element ? `${rock ? 'Aggregate' : 'Element'}: ${card.element}` : null,
      card.location ? `Location: ${card.location}` : null,
      card.pourDatetime ? `${rock ? 'Delivery' : 'Pour'} date/time: ${fmtDt(card.pourDatetime)}` : null,
      card.mix && !rock ? `Mix ID: ${card.mix}` : null,
      card.qtyText ? `Quantity: ${qtyDisplay(card)}` : null,
      card.costCode && rock ? `Cost code: ${card.costCode}` : null,
      card.placement ? `Placement: ${card.placement}` : null,
      card.superName ? `Field leader: ${card.superName}` : null,
      confirmedWith ? '' : null,
      confirmedWith ? `Order placed by phone ${confirmedWith}.` : null,
      notes ? `Call notes: ${notes}` : null,
      '',
      'Please reply to confirm. Thank you.',
    ].filter((l): l is string => l !== null);
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\r\n'))}`;
  };

  const full = build(card.orderNotes);
  if (full.length <= MAILTO_BUDGET || !card.orderNotes) return full;
  // Over budget: keep the email sendable by shortening the notes rather than
  // letting the client silently cut the body off mid-sentence. Binary-search
  // the longest note that fits — encodeURIComponent makes length non-linear.
  let lo = 0;
  let hi = card.orderNotes.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (build(`${card.orderNotes.slice(0, mid)}… (full notes on the pour card)`).length <= MAILTO_BUDGET) lo = mid;
    else hi = mid - 1;
  }
  // Nothing fits (the rest of the card is already long) — say where to look
  // rather than emitting a stub note that starts mid-word.
  if (lo === 0) return build('see the pour card in the Project Tracker');
  return build(`${card.orderNotes.slice(0, lo)}… (full notes on the pour card)`);
}

/**
 * The "which job?" picker data — the unscoped project list (GET /projects is
 * assignments-scoped, which breaks cross-project pickers) as Combobox options,
 * plus the label → project id lookup. One definition so every QA/QC picker
 * (assign a card, assign an inspection, add an order) labels jobs the same way
 * and a label typed in one always resolves.
 */
export function useJobPicker(enabled = true) {
  const { data } = useQuery({
    queryKey: ['qaqc-project-options'],
    queryFn: () => qaqcApi.projectOptions(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => {
    const idByLabel = new Map<string, number>();
    const options = (data ?? []).map((o) => {
      const value = `${o.jobNumber} — ${o.title}`;
      idByLabel.set(value, o.id);
      return { value };
    });
    return { options, idByLabel };
  }, [data]);
}

/** Office actions: Confirm / Email vendor / Mark poured / Cancel (will-call).
 *  Invalidates by key prefix so both the per-project tab (['qaqc-pours', id])
 *  and the roll-up page (['qaqc-pours', 'all']) refresh. */
export function PourAction({ card, jobNumber, size }: { card: PourCard; jobNumber: string; size?: 'sm' }) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
    qc.invalidateQueries({ queryKey: ['project-counts'] });
  };
  const confirmMut = useMutation({ mutationFn: () => qaqcApi.confirm(card.id), onSuccess: invalidate });
  const pouredMut = useMutation({ mutationFn: () => qaqcApi.markPoured(card.id), onSuccess: invalidate });
  const coveredMut = useMutation({ mutationFn: () => qaqcApi.markCovered(card.id), onSuccess: invalidate });
  const cancelMut = useMutation({ mutationFn: () => qaqcApi.cancel(card.id), onSuccess: invalidate });
  const isWillCall = card.pourType === 'willcall';

  // Any open order can be cancelled — a main pour called off for weather or
  // schedule, a will-call superseded, etc. (product owner, 2026-07-20). Only poured /
  // already-cancelled orders are locked (enforced server-side too).
  const showCancel = card.status === 'pending' || card.status === 'confirmed';

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {card.status === 'pending' && (
        <Button variant="primary" size={size} onClick={() => confirmMut.mutate()} isLoading={confirmMut.isPending}>Confirm order</Button>
      )}
      {card.status === 'confirmed' && (
        <>
          <a className="btn btn-secondary" style={{ fontSize: size === 'sm' ? 12 : undefined, textDecoration: 'none' }}
             href={vendorEmailHref(card, jobNumber)}
             title="Compose the vendor confirmation email in Outlook (add the vendor's address)">
            ✉ Email vendor
          </a>
          {isWillCall ? (
            <Button variant="secondary" size={size} onClick={() => coveredMut.mutate()} isLoading={coveredMut.isPending}
              title="The concrete came in with the main pour — removes this will-call from the schedule">
              Confirmed with main pour
            </Button>
          ) : (
            <Button variant="secondary" size={size} onClick={() => pouredMut.mutate()} isLoading={pouredMut.isPending}>{isRock(card) ? 'Mark delivered' : 'Mark poured'}</Button>
          )}
        </>
      )}
      {showCancel && (
        <Button variant="secondary" size={size} onClick={() => { if (confirm(isRock(card) ? 'Cancel this rock order?' : 'Cancel this pour order?')) cancelMut.mutate(); }} isLoading={cancelMut.isPending}>Cancel</Button>
      )}
    </span>
  );
}
